import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useContext,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';

import { cn } from '@/shared/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

/**
 * Layout del diálogo. `scrollable` activa el patrón empresarial de **tres
 * regiones** (header y footer fijos, body con scroll propio); por defecto el
 * diálogo entero scrollea (compat con los diálogos cortos del proyecto). El
 * contexto deja que `DialogHeader`/`DialogBody`/`DialogFooter` se auto-ajusten
 * sin que cada modal copie clases.
 */
const DialogLayoutContext = createContext<{ scrollable: boolean }>({ scrollable: false });

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

interface DialogContentProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Activa el layout de tres regiones (header/footer fijos + body scrollable).
   * Úselo en diálogos con contenido alto (formularios con dropzones, mapas,
   * listas) para que el footer de acciones nunca se salga del viewport y el
   * scroll quede contenido. Componer con `DialogBody` para la zona scrollable.
   */
  scrollable?: boolean;
}

/**
 * Dialog responsive empresarial:
 * - Mobile: 1rem de margen lateral (no toca bordes), rounded-2xl, max-height de
 *   viewport (dvh respeta la URL bar mobile) con scroll contenido.
 * - sm+: rounded-3xl del DS (24px), max-h respetando viewport.
 *
 * Dos modos:
 * - **Por defecto** (`scrollable=false`): el diálogo entero scrollea con padding
 *   propio (`p-4`/`p-6`). Ideal para diálogos cortos (confirmaciones, 1–2 campos).
 * - **`scrollable`**: columna flex sin padding propio; el padding lo aportan
 *   `DialogHeader`/`DialogBody`/`DialogFooter`. El header y el footer quedan
 *   fijos y solo el body scrollea (el botón de cerrar no se va con el scroll).
 *
 * El uso de `dvh` evita que el diálogo se corte cuando la barra de URL mobile
 * aparece/desaparece.
 */
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, scrollable = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogLayoutContext.Provider value={{ scrollable }}>
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl border bg-background shadow-lg duration-200 sm:rounded-[24px]',
          'max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)]',
          scrollable ? 'flex flex-col overflow-hidden' : 'grid gap-4 overflow-y-auto p-4 sm:p-6',
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-3 top-3 z-10 inline-flex size-9 items-center justify-center rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none sm:right-4 sm:top-4">
          <X className="size-4" />
          <span className="sr-only">Cerrar</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogLayoutContext.Provider>
  </DialogPortal>
));
DialogContent.displayName = 'DialogContent';

export const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { scrollable } = useContext(DialogLayoutContext);
  return (
    <div
      className={cn(
        'flex flex-col space-y-1.5 text-left',
        // `pr-8`/`pr-12`: holgura para el botón de cerrar (size-9 + offset).
        scrollable
          ? 'shrink-0 border-b border-border px-4 py-4 pr-12 sm:px-6 sm:py-5 sm:pr-14'
          : 'pr-8',
        className,
      )}
      {...props}
    />
  );
};

/**
 * Zona scrollable del diálogo en modo `scrollable`. Toma el alto disponible
 * entre el header y el footer fijos y desplaza solo su contenido. En modo por
 * defecto es un contenedor neutro (el scroll lo lleva el `DialogContent`).
 */
export const DialogBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { scrollable } = useContext(DialogLayoutContext);
  return (
    <div
      className={cn(
        scrollable && 'min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5',
        className,
      )}
      {...props}
    />
  );
};

/**
 * Footer empresarial 50/50 (mobile: stacked full-width, sm+: row distribuido).
 * Botones full-width en mobile asegura hit-targets ≥ 44px y consistencia con
 * patrones nativos (iOS/Android donde acciones primarias son full-width). En
 * modo `scrollable` queda fijo al pie con separador.
 */
export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { scrollable } = useContext(DialogLayoutContext);
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:gap-3 sm:[&>*]:flex-1',
        '[&>button]:w-full sm:[&>button]:w-auto',
        scrollable && 'shrink-0 border-t border-border px-4 py-4 sm:px-6 sm:py-5',
        className,
      )}
      {...props}
    />
  );
};

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';
